import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { LoadingOutlined, PauseOutlined, SoundOutlined } from '@ant-design/icons';

import { Button, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { useToast } from '../../../../lib/toast';
import { useNgheThuGiong } from '../../hooks/useBot';
import type { Giong } from '../../hooks/useChung';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';

/**
 * Chọn giọng đọc của bot: một ô chọn và một nút nghe thử ngay cạnh.
 *
 * Tám lựa chọn tên `Puck`, `Kore`, `Aoede` không nói được giọng nghe ra sao — ô chọn đứng một mình bắt người vận hành chọn
 * bừa rồi đợi KHÁCH THẬT vào khung chat mới biết. Ba giây nghe thay cho một vòng thử-và-sửa đi qua khách hàng.
 *
 * ⚠️ Mỗi lần nghe thử là một lượt gọi TTS tính tiền theo ký tự; câu mẫu do máy chủ cố định và ngắn.
 */
export function ChonGiongBot({
  danhSach, giong, onDoi, tat,
}: {
  danhSach: Giong[];
  /** `''` = để máy chủ chọn giọng đầu danh sách. */
  giong: string;
  onDoi: (ma: string) => void;
  tat?: boolean;
}) {
  const { t } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const thu = useNgheThuGiong();
  // Một thẻ audio dùng lại cho mọi lần nghe: đổi giọng rồi nghe ngay mà tiếng cũ còn chạy thì hai giọng chồng lên nhau.
  const loa = useRef<HTMLAudioElement | null>(null);
  const [dangPhat, setDangPhat] = useState(false);

  const dung = () => {
    loa.current?.pause();
    loa.current = null;
    setDangPhat(false);
  };
  // Rời màn giữa lúc đang phát thì tắt tiếng — không để một giọng đọc lơ lửng sau khi màn đã đóng.
  useEffect(() => () => loa.current?.pause(), []);

  const ngheThu = (ma: string) =>
    thu.mutate(ma, {
      onSuccess: (b64) => {
        dung();
        const am = new Audio(`data:audio/wav;base64,${b64}`);
        loa.current = am;
        setDangPhat(true);
        am.onended = () => setDangPhat(false);
        void am.play().catch(() => setDangPhat(false));
      },
      onError: (e) => toast.error(t('troLyAiBotGiong.ngheThuHong', { loi: moTaLoi(e) })),
    });

  if (!danhSach.length) {
    return <p className="text-xs leading-relaxed text-warning-700">{t('troLyAiBotGiong.chuaCoGiongCauHinh')}</p>;
  }

  // Giọng "mặc định" nghe thử được thì sẽ đọc một giọng rồi giao một giọng khác — còn tệ hơn không có nút nào.
  const ngheDuoc = Boolean(giong);
  const goiY = !ngheDuoc
    ? t('troLyAiBotGiong.ngheThuMacDinh')
    : dangPhat
      ? t('troLyAiBotGiong.dungNgheThu')
      : t('troLyAiBotGiong.ngheThuGiong');

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select<string>
        aria-label={t('troLyAiBotGiong.giongDoc')}
        disabled={tat}
        className="w-full sm:max-w-xs"
        value={giong}
        onChange={(v) => {
          dung();
          onDoi(String(v));
        }}
        options={[
          { value: '', label: t('troLyAiBotGiong.giongMacDinhChon') },
          ...danhSach.map((g) => ({ value: g.ma, label: g.nhan })),
        ]}
      />
      <Tooltip title={goiY}>
        {/* `span` bọc: nút `disabled` không phát sự kiện chuột, bỏ lớp bọc là mất lời giải thích vì sao nút mờ. */}
        <span className="inline-flex">
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            aria-label={goiY}
            disabled={tat || !ngheDuoc}
            icon={thu.isPending ? <LoadingOutlined /> : dangPhat ? <PauseOutlined /> : <SoundOutlined />}
            onClick={() => (dangPhat ? dung() : ngheThu(giong))}
          />
        </span>
      </Tooltip>
    </div>
  );
}
